import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { shortId } from '@/lib/format';

/** 解锁页（锁定态自动跳转；只展示 DID，绝不展示任何密钥）。 */
export function UnlockPage(): JSX.Element {
  const navigate = useNavigate();
  const status = useIdentityStore((s) => s.status);
  const setUnlocked = useIdentityStore((s) => s.setUnlocked);
  const setLocked = useIdentityStore((s) => s.setLocked);
  const pushError = useUiStore((s) => s.pushError);

  const [did, setDid] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const meta = await container.identity.loadMeta();
      if (meta) {
        setDid(meta.did);
        setLocked(meta);
      } else {
        navigate('/welcome', { replace: true });
      }
    })();
  }, [navigate, setLocked]);

  const submit = async (): Promise<void> => {
    if (!password) return;
    setSubmitting(true);
    setErrorText(null);
    try {
      const meta = await container.identity.unlock(password);
      setUnlocked(meta);
      navigate('/chat', { replace: true });
    } catch (err) {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      pushError(code);
      setErrorText(code === 'E_PASSWORD_INVALID' ? '口令错误，请重试' : '解锁失败，请重试');
    } finally {
      setSubmitting(false);
      setPassword('');
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="card w-[min(92vw,420px)] p-6">
        <h1 className="text-lg font-semibold text-text">解锁身份</h1>
        <p className="mt-1 text-sm text-dim">输入口令以解锁本机私钥。口令不会上传到任何地方。</p>

        <div className="mt-5 rounded-btn bg-panel-2 px-3 py-2">
          <p className="text-[11px] text-dim">当前身份</p>
          <p className="mono break-all text-xs text-text">{did || shortId('', 0, 0) || '加载中…'}</p>
        </div>

        <div className="mt-4 space-y-3">
          <Input
            type="password"
            autoFocus
            placeholder="口令"
            value={password}
            invalid={errorText !== null}
            hint={errorText ?? undefined}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password.length > 0) void submit();
            }}
          />
          <Button block loading={submitting} disabled={password.length === 0} onClick={() => void submit()}>
            解锁
          </Button>
        </div>

        <div className="mt-4 flex justify-between text-xs">
          <button type="button" className="text-dim hover:text-accent" onClick={() => navigate('/import')}>
            用助记词重新导入
          </button>
          {status === 'locked' ? <span className="text-dim">已锁定</span> : null}
        </div>
      </div>
    </div>
  );
}

export default UnlockPage;
